/**
 * Mechanical-keyboard clicks, tuned per switch profile so the sonic footprint
 * matches what a typist hears in real life:
 *   - **Cherry MX Blue**   — clicky · sharp click + prominent spring zing + thock (loudest, exam-lab default)
 *   - **Cherry MX Brown**  — tactile · softer click, muted spring, warm thock (office-friendly)
 *   - **Cherry MX Red**    — linear · no click, no spring, just a clean bottomout thock (silent typists)
 *
 * Every layer is per-profile-scaled so switching profiles gives a genuinely
 * different sound, not just louder/quieter. Each keystroke also schedules a
 * subtle release click 45-70 ms later.
 */

const KEY_ON = "stb_keyboard_sound";
const KEY_PROFILE = "stenosync.kbSwitch.v1";

let audioCtx = null;
let masterGain = null;
let unlocked = false;

/* ---------------- Persistence ---------------- */

export const SWITCH_PROFILES = [
  { id: "blue",  label: "Cherry MX Blue",  desc: "Clicky · loud, sharp, prominent spring zing" },
  { id: "brown", label: "Cherry MX Brown", desc: "Tactile · softer bump, warm thock, no click" },
  { id: "red",   label: "Cherry MX Red",   desc: "Linear · quiet · pure bottomout thock" },
];

const DEFAULT_PROFILE_ID = "blue";

export function isKeyboardSoundEnabled() {
  try { return localStorage.getItem(KEY_ON) === "on"; } catch { return false; }
}

export function setKeyboardSoundEnabled(on) {
  try {
    localStorage.setItem(KEY_ON, on ? "on" : "off");
    window.dispatchEvent(new CustomEvent("stb-keyboard-sound-change", { detail: !!on }));
  } catch { /* ignore */ }
}

export function getSwitchProfile() {
  try {
    const v = localStorage.getItem(KEY_PROFILE);
    if (SWITCH_PROFILES.some((p) => p.id === v)) return v;
  } catch { /* ignore */ }
  return DEFAULT_PROFILE_ID;
}

export function setSwitchProfile(id) {
  if (!SWITCH_PROFILES.some((p) => p.id === id)) return;
  try {
    localStorage.setItem(KEY_PROFILE, id);
    window.dispatchEvent(new CustomEvent("stb-kb-switch-change", { detail: id }));
  } catch { /* ignore */ }
}

/* ---------------- Audio context ---------------- */

function ensureCtx() {
  if (typeof window === "undefined") return null;
  if (!audioCtx) {
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      masterGain = audioCtx.createGain();
      masterGain.gain.value = 1.8;
      masterGain.connect(audioCtx.destination);
    } catch {
      return null;
    }
  }
  if (audioCtx.state === "suspended") audioCtx.resume().catch(() => {});
  return audioCtx;
}

if (typeof window !== "undefined") {
  const unlock = () => {
    if (unlocked) return;
    unlocked = true;
    ensureCtx();
  };
  window.addEventListener("pointerdown", unlock, { once: true, passive: true });
  window.addEventListener("keydown", unlock, { once: true, passive: true });
}

/* ---------------- Layer helpers ---------------- */

function noiseBuffer(ctx, ms) {
  const len = Math.max(1, Math.floor(ctx.sampleRate * (ms / 1000)));
  const b = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = b.getChannelData(0);
  for (let i = 0; i < len; i++) {
    d[i] = (Math.random() * 2 - 1) * Math.exp(-i / (len * 0.14));
  }
  return b;
}

/**
 * Per-profile scaling factors for each acoustic layer.
 * (click, thock, thump, spring, release)
 * A value of 0 mutes the layer entirely.
 */
const PROFILE_MIX = {
  blue:  { click: 1.00, thock: 1.00, thump: 1.00, spring: 1.00, release: 1.00 },
  brown: { click: 0.35, thock: 1.15, thump: 0.85, spring: 0.20, release: 0.65 },
  red:   { click: 0.00, thock: 1.30, thump: 1.10, spring: 0.00, release: 0.55 },
};

function scheduleClick(ctx, when, variant, mix) {
  // === Layer 1: HIGH click (contact / leaf-spring hit) ===
  if (mix.click > 0.01) {
    const nb = noiseBuffer(ctx, 22);
    const src = ctx.createBufferSource();
    src.buffer = nb;
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = variant % 2 === 0 ? 2400 : 2900;
    const peak = ctx.createBiquadFilter();
    peak.type = "peaking";
    peak.frequency.value = variant % 2 === 0 ? 4200 : 5100;
    peak.Q.value = 3.2;
    peak.gain.value = 11;
    const g1 = ctx.createGain();
    g1.gain.setValueAtTime(0.0001, when);
    g1.gain.exponentialRampToValueAtTime(0.55 * mix.click, when + 0.0008);
    g1.gain.exponentialRampToValueAtTime(0.0001, when + 0.03);
    src.connect(hp).connect(peak).connect(g1).connect(masterGain);
    src.start(when);
    src.stop(when + 0.04);
  }

  // === Layer 2: MID "thock" (keycap/plastic body) ===
  if (mix.thock > 0.01) {
    const osc = ctx.createOscillator();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(variant % 2 === 0 ? 320 : 380, when);
    osc.frequency.exponentialRampToValueAtTime(140, when + 0.05);
    const midFilter = ctx.createBiquadFilter();
    midFilter.type = "bandpass";
    midFilter.frequency.value = 500;
    midFilter.Q.value = 2.2;
    const g2 = ctx.createGain();
    g2.gain.setValueAtTime(0.0001, when);
    g2.gain.exponentialRampToValueAtTime(0.30 * mix.thock, when + 0.003);
    g2.gain.exponentialRampToValueAtTime(0.0001, when + 0.08);
    osc.connect(midFilter).connect(g2).connect(masterGain);
    osc.start(when);
    osc.stop(when + 0.09);
  }

  // === Layer 3: LOW thump (bottomout / desk resonance) ===
  if (mix.thump > 0.01) {
    const low = ctx.createOscillator();
    low.type = "sine";
    low.frequency.setValueAtTime(90, when);
    low.frequency.exponentialRampToValueAtTime(45, when + 0.04);
    const g3 = ctx.createGain();
    g3.gain.setValueAtTime(0.0001, when);
    g3.gain.exponentialRampToValueAtTime(0.22 * mix.thump, when + 0.002);
    g3.gain.exponentialRampToValueAtTime(0.0001, when + 0.06);
    low.connect(g3).connect(masterGain);
    low.start(when);
    low.stop(when + 0.07);
  }

  // === Layer 4: SPRING zing (metal leaf-spring ring — the Blue tell) ===
  if (mix.spring > 0.01) {
    const spring = ctx.createOscillator();
    spring.type = "sawtooth";
    spring.frequency.setValueAtTime(variant % 2 === 0 ? 1600 : 1780, when);
    spring.frequency.exponentialRampToValueAtTime(2300, when + 0.006);
    const springBP = ctx.createBiquadFilter();
    springBP.type = "bandpass";
    springBP.frequency.value = 2000;
    springBP.Q.value = 6;
    const g4 = ctx.createGain();
    g4.gain.setValueAtTime(0.0001, when);
    g4.gain.exponentialRampToValueAtTime(0.10 * mix.spring, when + 0.001);
    g4.gain.exponentialRampToValueAtTime(0.0001, when + 0.025);
    spring.connect(springBP).connect(g4).connect(masterGain);
    spring.start(when);
    spring.stop(when + 0.03);
  }
}

/** Play a full mechanical-keyboard keystroke (down + up click). */
export function playKeyClick(variant = 0, profileId = null) {
  if (!isKeyboardSoundEnabled()) return;
  const ctx = ensureCtx();
  if (!ctx) return;
  const mix = PROFILE_MIX[profileId || getSwitchProfile()] || PROFILE_MIX.blue;
  const now = ctx.currentTime;
  scheduleClick(ctx, now, variant, mix);
  // Release click — scaled by profile so Red / Brown are quieter on return.
  const releaseDelay = 0.055 + Math.random() * 0.02;
  if (mix.release > 0.01) {
    const releaseMix = {
      click: mix.click * mix.release * 0.55,
      thock: mix.thock * mix.release * 0.5,
      thump: 0,
      spring: mix.spring * mix.release * 0.4,
    };
    scheduleClick(ctx, now + releaseDelay, variant + 1, releaseMix);
  }
}

/** Special sound for Space bar — deeper stabiliser slap, scaled by profile. */
function playSpaceThock() {
  if (!isKeyboardSoundEnabled()) return;
  const ctx = ensureCtx();
  if (!ctx) return;
  const profileId = getSwitchProfile();
  const mix = PROFILE_MIX[profileId] || PROFILE_MIX.blue;
  const now = ctx.currentTime;
  scheduleClick(ctx, now, 0, mix);
  const low = ctx.createOscillator();
  low.type = "sine";
  low.frequency.setValueAtTime(70, now);
  low.frequency.exponentialRampToValueAtTime(38, now + 0.06);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, now);
  g.gain.exponentialRampToValueAtTime(0.45 * mix.thump, now + 0.003);
  g.gain.exponentialRampToValueAtTime(0.0001, now + 0.09);
  low.connect(g).connect(masterGain);
  low.start(now);
  low.stop(now + 0.11);
  if (mix.release > 0.01) {
    scheduleClick(ctx, now + 0.07, 1, { ...mix, thump: 0 });
  }
}

/** Convenience: attach to a textarea/input. Returns an onKeyDown handler. */
export function keyClickHandler() {
  let n = 0;
  return (e) => {
    if (e && (e.metaKey || e.ctrlKey || e.altKey)) return;
    const key = e?.key;
    if (key === "Shift" || key === "CapsLock" || key === "Alt" ||
        key === "Meta" || key === "Control") return;
    if (key === " " || key === "Spacebar") { playSpaceThock(); return; }
    n = (n + 1) & 1;
    playKeyClick(n);
  };
}
